import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Stories.css';
import ProfileMenu from '../ProfileMenu';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { useLanguage } from '../i18n/LanguageContext';

const TEXT = {
    en: { title: 'Stories', subtitle: 'Reading at your level', classic: 'Classic tales', original: 'Original stories' },
    it: { title: 'Storie', subtitle: 'Lettura al tuo livello', classic: 'Fiabe classiche', original: 'Storie originali' },
    es: { title: 'Historias', subtitle: 'Lectura a tu nivel', classic: 'Cuentos clásicos', original: 'Historias originales' },
    fr: { title: 'Histoires', subtitle: 'Lecture à ton niveau', classic: 'Contes classiques', original: 'Histoires originales' },
    pt: { title: 'Histórias', subtitle: 'Leitura no seu nível', classic: 'Contos clássicos', original: 'Histórias originais' },
};

// The original story ideas are written for Italy. This adapts them to the language being learned.
const localizeBrief = (brief, lang) => {
    if (!brief) return brief;
    const name = lang.charAt(0).toUpperCase() + lang.slice(1);
    return brief.replace(/Italian/g, name).replace(/\ban (Spanish|French|Portuguese)\b/g, 'a $1');
};

// "title" stays in English because it is what the story writer is given.
// "names" and "descs" are what the learner sees, in their interface language.
const classicStories = [
    {
        slug: 'pinocchio', title: 'Pinocchio', emoji: '🪵',
        names: { en: 'Pinocchio', it: 'Pinocchio', es: 'Pinocho', fr: 'Pinocchio', pt: 'Pinóquio' },
        descs: {
            en: 'A wooden puppet who dreams of becoming a real boy',
            it: 'Un burattino di legno che sogna di diventare un bambino vero',
            es: 'Un títere de madera que sueña con ser un niño de verdad',
            fr: 'Une marionnette en bois qui rêve de devenir un vrai petit garçon',
            pt: 'Um boneco de madeira que sonha em se tornar um menino de verdade',
        },
    },
    {
        slug: 'cenerentola', title: 'Cinderella', emoji: '👠',
        names: { en: 'Cinderella', it: 'Cenerentola', es: 'La Cenicienta', fr: 'Cendrillon', pt: 'Cinderela' },
        descs: {
            en: 'A kind girl, a lost shoe, and a magical night',
            it: 'Una ragazza gentile, una scarpetta perduta e una notte magica',
            es: 'Una chica amable, un zapato perdido y una noche mágica',
            fr: 'Une gentille jeune fille, une chaussure perdue et une nuit magique',
            pt: 'Uma garota gentil, um sapato perdido e uma noite mágica',
        },
    },
    {
        slug: 'cappuccetto-rosso', title: 'Little Red Riding Hood', emoji: '🧺',
        names: { en: 'Little Red Riding Hood', it: 'Cappuccetto Rosso', es: 'Caperucita Roja', fr: 'Le Petit Chaperon rouge', pt: 'Chapeuzinho Vermelho' },
        descs: {
            en: 'A girl, a forest, and a wolf in disguise',
            it: 'Una bambina, un bosco e un lupo travestito',
            es: 'Una niña, un bosque y un lobo disfrazado',
            fr: 'Une fillette, une forêt et un loup déguisé',
            pt: 'Uma menina, uma floresta e um lobo disfarçado',
        },
    },
    {
        slug: 'bella-e-la-bestia', title: 'Beauty and the Beast', emoji: '🌹',
        names: { en: 'Beauty and the Beast', it: 'La Bella e la Bestia', es: 'La Bella y la Bestia', fr: 'La Belle et la Bête', pt: 'A Bela e a Fera' },
        descs: {
            en: 'A curse, a rose, and a love that breaks it',
            it: 'Una maledizione, una rosa e un amore che la spezza',
            es: 'Una maldición, una rosa y un amor que la rompe',
            fr: 'Une malédiction, une rose et un amour qui la brise',
            pt: 'Uma maldição, uma rosa e um amor que a quebra',
        },
    },
    {
        slug: 'i-tre-porcellini', title: 'The Three Little Pigs', emoji: '🐷',
        names: { en: 'The Three Little Pigs', it: 'I tre porcellini', es: 'Los tres cerditos', fr: 'Les Trois Petits Cochons', pt: 'Os Três Porquinhos' },
        descs: {
            en: 'Three houses, one wolf, and a lot of huffing',
            it: 'Tre case, un lupo e un sacco di soffi',
            es: 'Tres casas, un lobo y muchos soplidos',
            fr: 'Trois maisons, un loup et beaucoup de souffles',
            pt: 'Três casas, um lobo e muito sopro',
        },
    },
    {
        slug: 'hansel-e-gretel', title: 'Hansel and Gretel', emoji: '🍬',
        names: { en: 'Hansel and Gretel', it: 'Hänsel e Gretel', es: 'Hansel y Gretel', fr: 'Hansel et Gretel', pt: 'João e Maria' },
        descs: {
            en: 'Two children lost in the woods find a strange house',
            it: 'Due bambini persi nel bosco trovano una casa strana',
            es: 'Dos niños perdidos en el bosque encuentran una casa extraña',
            fr: 'Deux enfants perdus dans les bois trouvent une drôle de maison',
            pt: 'Duas crianças perdidas na floresta encontram uma casa estranha',
        },
    },
    {
        slug: 'il-brutto-anatroccolo', title: 'The Ugly Duckling', emoji: '🦢',
        names: { en: 'The Ugly Duckling', it: 'Il brutto anatroccolo', es: 'El patito feo', fr: 'Le Vilain Petit Canard', pt: 'O Patinho Feio' },
        descs: {
            en: 'A duckling who doesn\'t feel like he belongs',
            it: 'Un anatroccolo che non si sente al suo posto',
            es: 'Un patito que siente que no encaja',
            fr: 'Un caneton qui ne se sent pas à sa place',
            pt: 'Um patinho que sente que não pertence',
        },
    },
    {
        slug: 'riccioli-doro', title: 'Goldilocks and the Three Bears', emoji: '🐻',
        names: { en: 'Goldilocks and the Three Bears', it: 'Riccioli d’oro e i tre orsi', es: 'Ricitos de Oro y los tres osos', fr: 'Boucle d’or et les trois ours', pt: 'Cachinhos Dourados e os Três Ursos' },
        descs: {
            en: 'An uninvited guest tries everything in the house',
            it: 'Un’ospite non invitata prova tutto quello che c’è in casa',
            es: 'Una invitada inesperada lo prueba todo en la casa',
            fr: 'Une invitée surprise essaie tout dans la maison',
            pt: 'Uma visitante sem convite experimenta tudo na casa',
        },
    },
];

const originalStories = [
    {
        slug: 'il-mercato-di-verona', title: 'The Market of Verona', emoji: '🍅',
        names: { en: 'The Market of Verona', it: 'Il mercato di Verona', es: 'El mercado de Verona', fr: 'Le marché de Vérone', pt: 'O mercado de Verona' },
        descs: {
            en: 'A morning of errands in an Italian piazza',
            it: 'Una mattina di commissioni in una piazza italiana',
            es: 'Una mañana de recados en una plaza italiana',
            fr: 'Une matinée de courses sur une place italienne',
            pt: 'Uma manhã de compras em uma praça italiana',
        },
        brief: 'Write a warm, everyday slice-of-life story about a person doing their weekly shopping at an outdoor market in an Italian town.',
    },
    {
        slug: 'lettera-da-milano', title: 'A Letter from Milan', emoji: '✉️',
        names: { en: 'A Letter from Milan', it: 'Una lettera da Milano', es: 'Una carta desde Milán', fr: 'Une lettre de Milan', pt: 'Uma carta de Milão' },
        descs: {
            en: 'An old letter connects two generations',
            it: 'Una vecchia lettera unisce due generazioni',
            es: 'Una carta antigua une a dos generaciones',
            fr: 'Une vieille lettre relie deux générations',
            pt: 'Uma carta antiga une duas gerações',
        },
        brief: 'Write a gentle, emotional short story about someone finding an old letter written by a family member, and what it reveals.',
    },
    {
        slug: 'il-treno-in-ritardo', title: 'The Delayed Train', emoji: '🚆',
        names: { en: 'The Delayed Train', it: 'Il treno in ritardo', es: 'El tren retrasado', fr: 'Le train en retard', pt: 'O trem atrasado' },
        descs: {
            en: 'A missed connection turns into an unexpected friendship',
            it: 'Una coincidenza persa diventa un’amicizia inaspettata',
            es: 'Una conexión perdida se convierte en una amistad inesperada',
            fr: 'Une correspondance manquée devient une amitié inattendue',
            pt: 'Uma conexão perdida vira uma amizade inesperada',
        },
        brief: 'Write a light, feel-good story about two strangers who meet while waiting for a delayed train and end up talking for hours.',
    },
    {
        slug: 'la-ricetta-della-nonna', title: 'Grandmother\'s Recipe', emoji: '🍝',
        names: { en: 'Grandmother\'s Recipe', it: 'La ricetta della nonna', es: 'La receta de la abuela', fr: 'La recette de grand-mère', pt: 'A receita da vovó' },
        descs: {
            en: 'A family recipe passed down through generations',
            it: 'Una ricetta di famiglia tramandata di generazione in generazione',
            es: 'Una receta familiar que pasa de generación en generación',
            fr: 'Une recette de famille transmise de génération en génération',
            pt: 'Uma receita de família passada de geração em geração',
        },
        brief: 'Write a heartwarming story about a grandchild learning to cook their grandmother\'s signature recipe.',
    },
    {
        slug: 'il-primo-giorno', title: 'The First Day', emoji: '🎒',
        names: { en: 'The First Day', it: 'Il primo giorno', es: 'El primer día', fr: 'Le premier jour', pt: 'O primeiro dia' },
        descs: {
            en: 'Starting something new in a new city',
            it: 'Iniziare qualcosa di nuovo in una nuova città',
            es: 'Empezar algo nuevo en una ciudad nueva',
            fr: 'Commencer quelque chose de nouveau dans une nouvelle ville',
            pt: 'Começar algo novo em uma cidade nova',
        },
        brief: 'Write an encouraging story about someone starting a new job or new school in an unfamiliar Italian city, and the small kindnesses that help them settle in.',
    },
    {
        slug: 'una-serata-al-cinema', title: 'A Night at the Cinema', emoji: '🎬',
        names: { en: 'A Night at the Cinema', it: 'Una serata al cinema', es: 'Una noche en el cine', fr: 'Une soirée au cinéma', pt: 'Uma noite no cinema' },
        descs: {
            en: 'An evening out turns memorable',
            it: 'Una serata fuori che diventa memorabile',
            es: 'Una salida nocturna que se vuelve memorable',
            fr: 'Une soirée qui devient mémorable',
            pt: 'Uma saída à noite que se torna memorável',
        },
        brief: 'Write a light, funny story about a group of friends going to the cinema and everything that goes slightly wrong.',
    },
];

export default function Stories() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('classic');
    const displayName = localStorage.getItem('vozoly_display_name') || 'U';
    const userInitial = displayName[0].toUpperCase();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key) => TEXT[uiLang][key];

    const list = tab === 'classic' ? classicStories : originalStories;

    return (
        <div className="st-screen">
            <div className="st-top-bar">
                <div className="st-top-row">
                    <button className="st-back" onClick={() => navigate('/home')}>✕</button>
                    <div className="st-title">{s('title')}</div>
                    <ProfileMenu initial={userInitial} />
                </div>
                <div className="st-subtitle">{s('subtitle')} — {level}</div>
            </div>

            <div className="st-tabs">
                <div className={`st-tab ${tab === 'classic' ? 'active' : ''}`} onClick={() => setTab('classic')}>
                    {s('classic')}
                </div>
                <div className={`st-tab ${tab === 'original' ? 'active' : ''}`} onClick={() => setTab('original')}>
                    {s('original')}
                </div>
            </div>

            <div className="st-list">
                {list.map(story => (
                    <div
                        key={story.slug}
                        className="st-card"
                        onClick={() => navigate(`/story/${story.slug}`, {
                            state: {
                                title: story.title,
                                displayTitle: story.names[uiLang],
                                brief: localizeBrief(story.brief, targetLang),
                                isClassic: tab === 'classic',
                            },
                        })}
                    >
                        <div className="st-card-emoji">{story.emoji}</div>
                        <div className="st-card-info">
                            <div className="st-card-title">{story.names[uiLang]}</div>
                            <div className="st-card-desc">{story.descs[uiLang]}</div>
                        </div>
                        <span className="st-card-arrow">›</span>
                    </div>
                ))}
            </div>

            <div style={{ height: '32px' }}></div>
        </div>
    );
}